"use client";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { ViewDeskIcon } from "@cmp-src/icons/TacticIcons/VIewDeskIcon";
import { Button } from "@cmp-src/primitives/button";
import type { IDedicatedResourceRef } from "@gettactic/api";
import { createMapLink } from "@gettactic/helpers";
import { dedicatedSeatCount } from "@gettactic/helpers/src/resources/dedicatedSeats";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { useAreas, useAuthenticated } from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import { UserCheck } from "lucide-react";
import React from "react";
import { DedicatedReleases } from "@/features/map/components/DedicatedReleases";
import { useResource } from "@/features/reserve/hooks/useResource";
import { useRouter } from "@/lib/useRouter";
import { MapWithMiniOfficePhoto } from "./DashboardEventDetails/MapWithMiniOfficePhoto";

export interface DashboardDedicatedDeskDetailsProps {
  desk: IDedicatedResourceRef;
  /** The agenda day the desk was opened from, `yyyy-MM-dd`. */
  date: string;
}

/**
 * The dashboard's details column for the person's own dedicated desk: where
 * it is on the map, its office and floor, and the days it is released to
 * others. There is nothing to book here, the desk is already theirs.
 */
export const DashboardDedicatedDeskDetails: React.FC<
  DashboardDedicatedDeskDetailsProps
> = ({ desk, date }) => {
  const router = useRouter();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const officeId = desk.office_id ?? "";
  const floorId = desk.parent_id ?? "";
  const { resource } = useResource({ id: desk.id });
  const { areas } = useAreas(officeId);
  const officeName = authenticatedUser.offices?.byId[officeId]?.name;
  const officeTimeZone = authenticatedUser.offices?.byId[officeId]?.time_zone;
  const floorName = floorId ? areas?.byId[floorId]?.name : undefined;
  const coverUrl = resource ? getResourceCoverUrl(resource) : undefined;
  const day = parseISO(`${date}T00:00:00`);

  const locate = () => {
    router.push(
      createMapLink({
        date,
        officeId,
        resourceId: desk.id,
        floorId: floorId || undefined
      })
    );
  };

  return (
    <div className="mb-4 flex w-full flex-col md:mt-3.5 md:mb-16">
      <div className="hidden flex-row items-center justify-between pb-4 md:flex">
        <div>
          <div className="v2-header-1 font-bold font-mona text-[32px]">
            {format(day, "EEEE")}
          </div>
          <p className="text-neutral-600 text-sm">{format(day, "MMMM d")}</p>
        </div>
        {officeId && (
          <Button onClick={locate}>
            <ViewDeskIcon />
            Locate
          </Button>
        )}
      </div>
      {officeId && floorId && (
        <MapWithMiniOfficePhoto
          resourceId={desk.id}
          officeId={officeId}
          parentId={floorId}
          startDate={date}
          resourceImage={coverUrl}
        />
      )}
      <div className="flex flex-col px-2.5 pt-4 md:px-0 md:pt-[30px]">
        <div className="font-bold text-lg">{desk.name}</div>
        <ResourceLocation
          location={officeName || "Location"}
          floor={floorName || "Floor"}
          allowWrap
        />
      </div>
      <div className="mx-2.5 mt-5 flex items-start gap-3 rounded-lg border border-neutral-200 p-4 md:mx-0">
        <UserCheck className="mt-0.5 size-5 shrink-0 text-neutral-500" />
        <div className="text-neutral-700 text-sm">
          <p className="font-medium text-neutral-900">Your dedicated desk</p>
          <p>
            It is yours on every day you are at {officeName ?? "the office"},
            with nothing to reserve. Release it while you are away and others
            can book it those days.
          </p>
        </div>
      </div>
      <div className="px-2.5 md:px-0">
        <DedicatedReleases
          resourceId={desk.id}
          emptyLabel="Not released on any day yet."
          timeZone={officeTimeZone}
          // No canSeeDetails: the viewer is an owner, recognised per release,
          // so on a shared workspace they read their own seat's details only.
          showsSeatOwner={!!resource && dedicatedSeatCount(resource) > 1}
        />
      </div>
    </div>
  );
};
